import { useCallback, useEffect, useRef, useState } from 'react';
import { Bell } from 'lucide-react';
import { formatInTimezone, useTimezone } from '@kioskgaming/ui';
import { STORAGE_KEYS } from '../constants';
import { ackAgentBroadcast, fetchAgentBroadcasts, type AdminBroadcastRow } from '../services/api';
import BroadcastMessageBody from './BroadcastMessageBody';

const POLL_MS = 2 * 60 * 1000;

function formatBroadcastTime(iso: string | null | undefined, timezone: string): string {
  if (iso == null || iso === '') return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = diffMs / (1000 * 60 * 60);

  if (diffHours < 24) {
    return `Today, ${formatInTimezone(date, timezone)}`;
  }
  if (diffHours < 48) {
    return `Yesterday, ${formatInTimezone(date, timezone)}`;
  }
  return formatInTimezone(date, timezone);
}

export default function AgentNotificationBell() {
  const { timezone } = useTimezone();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<AdminBroadcastRow[]>([]);
  const [loading, setLoading] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    if (!token) {
      setItems([]);
      return;
    }
    setLoading(true);
    try {
      const res = await fetchAgentBroadcasts();
      if (res.success && res.data?.broadcasts) {
        setItems(res.data.broadcasts);
      }
    } catch {
      // 401 is handled globally by auth interceptor/event; avoid unhandled promise noise.
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const unreadCount = items.filter((b) => !b.acknowledged).length;

  const handleRead = useCallback(async (id: string, isRead: boolean) => {
    if (isRead) return;
    try {
      await ackAgentBroadcast(id);
      await load();
    } catch {
      // noop: auth layer already reacts to unauthorized responses.
    }
  }, [load]);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => {
          setOpen((o) => !o);
        }}
        className="relative flex h-10 w-10 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 shadow-sm transition hover:bg-gray-50 hover:text-gray-900"
        aria-expanded={open}
        aria-label="Notifications"
      >
        <Bell className="h-5 w-5" strokeWidth={2} />
        {unreadCount > 0 ? (
          <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-[#f06522] px-1 text-[10px] font-bold text-white">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        ) : null}
      </button>

      {open ? (
        <div
          className="absolute right-0 top-[calc(100%+8px)] z-50 w-[min(100vw-1.5rem,20rem)] overflow-hidden rounded-lg border border-gray-200 bg-white text-gray-900 shadow-lg animate-in fade-in duration-150"
          role="dialog"
          aria-label="Notification list"
        >
          <div className="flex items-center gap-2 border-b border-gray-200 px-4 py-3">
            <p className="text-sm font-medium text-gray-900">Notifications</p>
            {unreadCount > 0 ? (
              <span className="rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-semibold text-orange-700">
                {unreadCount} new
              </span>
            ) : null}
          </div>
          <div className="max-h-[min(70vh,22rem)] overflow-y-auto">
            {loading && items.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-gray-500">Loading…</p>
            ) : items.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-gray-500">
                No notifications yet
              </p>
            ) : (
              <ul>
                {items.map((b) => {
                  const timeLabel = formatBroadcastTime(b.createdAt, timezone);
                  const isRead = Boolean(b.acknowledged);
                  return (
                    <li key={b.id}>
                      <div
                        role="button"
                        tabIndex={0}
                        className="flex w-full cursor-pointer items-start gap-2.5 border-b border-gray-100 px-4 py-3 text-left transition last:border-b-0 hover:bg-gray-50"
                        style={{ opacity: isRead ? 0.55 : 1 }}
                        onClick={() => void handleRead(b.id, isRead)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            void handleRead(b.id, isRead);
                          }
                        }}
                      >
                        <span
                          className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#f06522]"
                          style={{ background: isRead ? 'transparent' : undefined }}
                        />
                        <span className="min-w-0 flex-1">
                          <span className="mb-0.5 block text-sm font-medium text-gray-900">
                            {b.title}
                          </span>
                          <span className="mb-1 block whitespace-pre-wrap text-xs leading-relaxed text-gray-600">
                          <BroadcastMessageBody
                            text={b.body}
                            linkClassName="break-all text-orange-600 underline-offset-2 hover:underline"
                            onLinkClick={(e) => e.stopPropagation()}
                          />
                          </span>
                        {timeLabel ? (
                            <span className="block text-[11px] text-gray-400">
                              {timeLabel}
                            </span>
                        ) : null}
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
